import ClientCredit from '../components/ClientCredit';
import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ShoppingCart, Package, Image as ImageIcon, List, Plus, Minus, Trash2,
  FileText, Search, Home, DollarSign, Clock, Truck, CheckCircle2, CreditCard, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientPortal() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [mySales, setMySales] = useState([]);
  const [finance, setFinance] = useState(null);
  const [myClient, setMyClient] = useState(null);
  const [tab, setTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [detailSale, setDetailSale] = useState(null);

  useEffect(() => { init(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const init = async () => {
    try {
      const clientsRes = await api.getClients();
      const me = clientsRes.data[0];
      setMyClient(me);
      const allowed = me?.allowed_companies?.length ? me.allowed_companies : (me?.company_id ? [me.company_id] : (user?.allowed_companies || [user?.company_id].filter(Boolean)));
      const compsRes = await api.getCompanies();
      const filtered = compsRes.data.filter(c => allowed.includes(c.company_id) && c.active);
      setCompanies(filtered);
      if (filtered.length > 0) setSelectedCompany(filtered[0].company_id);
      if (me) {
        const fin = await api.getClientFinance(me.client_id);
        setFinance(fin.data);
      }
    } catch (e) {
      console.error(e);
      toast.error('SalesPRO: Error al cargar tu portal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompany) loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  useEffect(() => {
    if (tab === 'orders' && myClient) loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, myClient, selectedCompany]);

  const loadProducts = async () => {
    try {
      const res = await api.getProducts({ company_id: selectedCompany });
      setProducts(res.data);
    } catch (e) { /* ignore */ }
  };

  const loadSales = async () => {
    try {
      const res = await api.getSales();
      setMySales(res.data);
    } catch (e) { /* ignore */ }
  };

  const filteredProducts = products.filter(p =>
    !search ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.product_type.toLowerCase().includes(search.toLowerCase()) ||
    p.model.toLowerCase().includes(search.toLowerCase())
  );

  const cartTotal = cart.reduce((s, it) => s + it.price * it.quantity, 0);
  const cartCount = cart.reduce((s, it) => s + it.quantity, 0);

  const addToCart = (product, qty = 1) => {
    setCart(prev => {
      const ex = prev.find(it => it.product_id === product.product_id);
      if (ex) return prev.map(it => it.product_id === product.product_id ? { ...it, quantity: it.quantity + qty } : it);
      return [...prev, { product_id: product.product_id, name: product.name, price: product.price, quantity: qty }];
    });
    toast.success(`SalesPRO: ${product.name} agregado`);
  };

  const updateQty = (pid, delta) => {
    setCart(prev => prev.map(it => {
      if (it.product_id !== pid) return it;
      const q = it.quantity + delta;
      return q <= 0 ? null : { ...it, quantity: q };
    }).filter(Boolean));
  };

  const removeFromCart = (pid) => setCart(prev => prev.filter(it => it.product_id !== pid));

  const placeOrder = async () => {
    if (cart.length === 0) { toast.error('SalesPRO: El carrito está vacío'); return; }
    if (!myClient) { toast.error('SalesPRO: No se encontró tu perfil de cliente'); return; }
    try {
      await api.createSale({
        client_id: myClient.client_id,
        items: cart,
        total: cartTotal,
        status: 'pendiente_procesar',
        payment_status: 'pendiente',
        company_id: selectedCompany
      });
      toast.success('SalesPRO: ¡Pedido enviado! Tu vendedor lo procesará en breve.');
      setCart([]);
      setTab('orders');
      // Refrescar finanzas
      const fin = await api.getClientFinance(myClient.client_id);
      setFinance(fin.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al confirmar pedido');
    }
  };

  const downloadTicket = async (saleId) => {
    try {
      const res = await api.getSaleTicket(saleId);
      window.open(window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch { toast.error('SalesPRO: Error al descargar ticket'); }
  };

  const statusBadge = (status) => {
    const map = {
      'abierto': { color: 'bg-gray-100 text-gray-800', label: 'Abierto' },
      'pendiente_procesar': { color: 'bg-orange-100 text-orange-800', label: 'En revisión' },
      'procesada': { color: 'bg-blue-100 text-blue-800', label: 'Confirmada' },
      'pendiente_despacho': { color: 'bg-orange-100 text-orange-800', label: 'Por despachar' },
      'en_transito': { color: 'bg-purple-100 text-purple-800', label: 'En camino' },
      'despachado': { color: 'bg-green-100 text-green-800', label: 'Entregada' },
      'cancelado': { color: 'bg-red-100 text-red-800', label: 'Cancelada' }
    };
    return map[status] || map['abierto'];
  };

  if (loading) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  return (
    <div className="space-y-4" data-testid="client-portal">
      <ClientCredit />
      {/* Header con selector de empresa */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">¡Hola, {user?.full_name?.split(' ')[0]}!</h1>
          <p className="text-sm text-[#64748B]">Bienvenido a tu portal SalesPRO</p>
        </div>
        {companies.length > 0 && (
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
            data-testid="client-company-selector"
          >
            {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
        )}
      </div>

      {/* Tabs — scroll horizontal en móvil */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {[
          { id: 'home', icon: Home, label: 'Inicio' },
          { id: 'catalog', icon: Package, label: 'Catálogo' },
          { id: 'cart', icon: ShoppingCart, label: `Carrito (${cartCount})` },
          { id: 'orders', icon: FileText, label: 'Mis pedidos' }
        ].map(t => {
          const Icon = t.icon;
          return (
            <Button
              key={t.id}
              size="sm"
              variant={tab === t.id ? 'default' : 'outline'}
              onClick={() => setTab(t.id)}
              data-testid={`portal-tab-${t.id}`}
              className="shrink-0"
            >
              <Icon size={14} className="mr-1" /> {t.label}
            </Button>
          );
        })}
      </div>

      {/* HOME - Resumen financiero */}
      {tab === 'home' && finance && (
        <div className="space-y-4">
          {/* Cards financieras */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="border border-[#E2E8F0] bg-gradient-to-br from-[#004D40] to-[#00796B] text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-white/70 text-xs uppercase mb-1"><DollarSign size={12} />Total comprado</div>
                <p className="text-xl sm:text-2xl font-bold">${finance.totals.invoiced.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className={`border border-[#E2E8F0] ${finance.totals.debt > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs uppercase mb-1">
                  <AlertCircle size={12} className={finance.totals.debt > 0 ? 'text-red-600' : 'text-green-600'} />
                  {finance.totals.debt > 0 ? 'Por pagar' : 'Al día'}
                </div>
                <p className={`text-xl sm:text-2xl font-bold ${finance.totals.debt > 0 ? 'text-red-600' : 'text-green-700'}`}>
                  ${finance.totals.pending.toFixed(2)}
                </p>
              </CardContent>
            </Card>
            <Card className="border border-[#E2E8F0]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase mb-1"><CreditCard size={12} />Pagado</div>
                <p className="text-xl sm:text-2xl font-bold">${finance.totals.paid.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="border border-[#E2E8F0]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase mb-1"><FileText size={12} />Total compras</div>
                <p className="text-xl sm:text-2xl font-bold">{finance.count.total_invoices}</p>
              </CardContent>
            </Card>
          </div>

          {/* Estados de compras */}
          <Card className="border border-[#E2E8F0]">
            <CardContent className="p-4">
              <h3 className="font-heading font-bold mb-3 text-sm">Estado de mis pedidos</h3>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <Clock size={20} className="mx-auto text-orange-600 mb-1" />
                  <p className="text-2xl font-bold text-orange-700">{finance.count.open_orders}</p>
                  <p className="text-[10px] text-orange-700 uppercase">Abiertos</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <Truck size={20} className="mx-auto text-purple-600 mb-1" />
                  <p className="text-2xl font-bold text-purple-700">{finance.count.in_transit}</p>
                  <p className="text-[10px] text-purple-700 uppercase">En camino</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <CheckCircle2 size={20} className="mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-700">{finance.count.delivered}</p>
                  <p className="text-[10px] text-green-700 uppercase">Entregados</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA al catálogo */}
          <Button onClick={() => setTab('catalog')} className="w-full h-12" data-testid="cta-shop-now">
            <Package size={18} className="mr-2" /> Ver catálogo y comprar
          </Button>
        </div>
      )}

      {/* CATÁLOGO */}
      {tab === 'catalog' && (
        <>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input
                placeholder="Buscar producto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-11"
                data-testid="catalog-search"
              />
            </div>
            <Button variant={view === 'grid' ? 'default' : 'outline'} size="icon" onClick={() => setView('grid')} data-testid="view-photos">
              <ImageIcon size={16} />
            </Button>
            <Button variant={view === 'list' ? 'default' : 'outline'} size="icon" onClick={() => setView('list')} data-testid="view-list">
              <List size={16} />
            </Button>
          </div>

          {view === 'grid' ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {filteredProducts.map(p => (
                <Card key={p.product_id} className="border border-[#E2E8F0] overflow-hidden flex flex-col" data-testid={`product-${p.product_id}`}>
                  <div className="aspect-square bg-[#F1F5F9] flex items-center justify-center overflow-hidden">
                    {p.photos?.[0] ? (
                      <img src={p.photos[0]} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <Package size={36} className="text-[#CBD5E1]" />
                    )}
                  </div>
                  <CardContent className="p-3 flex-1 flex flex-col">
                    <p className="font-semibold text-sm leading-tight line-clamp-2">{p.name}</p>
                    <p className="text-[11px] text-[#94A3B8] mb-2">{p.product_type}</p>
                    <div className="mt-auto flex items-center justify-between gap-2">
                      <span className="font-bold text-[#022C22]">${p.price.toFixed(2)}</span>
                      <Button size="sm" onClick={() => addToCart(p)} data-testid={`add-cart-${p.product_id}`} className="h-8 px-2">
                        <Plus size={14} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredProducts.length === 0 && (
                <p className="col-span-full text-center text-[#94A3B8] py-8">Sin productos disponibles</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredProducts.map(p => (
                <Card key={p.product_id} className="border border-[#E2E8F0]" data-testid={`product-list-${p.product_id}`}>
                  <CardContent className="p-3 flex gap-3 items-center">
                    <div className="w-16 h-16 bg-[#F1F5F9] flex items-center justify-center rounded shrink-0 overflow-hidden">
                      {p.photos?.[0] ? <img src={p.photos[0]} alt={p.name} className="w-full h-full object-cover" /> : <Package size={24} className="text-[#CBD5E1]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{p.name}</p>
                      <p className="text-xs text-[#64748B] truncate">{p.product_type} · {p.model}</p>
                      <p className="font-bold mt-1">${p.price.toFixed(2)}</p>
                    </div>
                    <Button size="sm" onClick={() => addToCart(p)}><Plus size={14} className="mr-1" /> Añadir</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* CARRITO */}
      {tab === 'cart' && (
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            {cart.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart size={48} className="mx-auto text-[#CBD5E1] mb-3" />
                <p className="text-[#64748B]">Tu carrito está vacío</p>
                <Button onClick={() => setTab('catalog')} className="mt-4">Ver catálogo</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map(it => (
                  <div key={it.product_id} className="flex items-center gap-3 pb-3 border-b" data-testid={`cart-item-${it.product_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{it.name}</p>
                      <p className="text-xs text-[#64748B]">${it.price.toFixed(2)} c/u</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateQty(it.product_id, -1)}><Minus size={12} /></Button>
                      <span className="font-mono w-8 text-center">{it.quantity}</span>
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateQty(it.product_id, 1)}><Plus size={12} /></Button>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold">${(it.price * it.quantity).toFixed(2)}</p>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeFromCart(it.product_id)}>
                        <Trash2 size={12} className="text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-3">
                  <span className="font-semibold">Total</span>
                  <span className="text-2xl font-bold">${cartTotal.toFixed(2)}</span>
                </div>
                <Button onClick={placeOrder} className="w-full h-12 text-base" data-testid="place-order-button">
                  Confirmar pedido
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PEDIDOS */}
      {tab === 'orders' && (
        <div className="space-y-2">
          {mySales.length === 0 ? (
            <p className="text-center text-[#94A3B8] py-12">Aún no tienes pedidos</p>
          ) : (
            mySales.map(sale => {
              const b = statusBadge(sale.status);
              return (
                <Card key={sale.sale_id} className="border border-[#E2E8F0] cursor-pointer" data-testid={`order-${sale.sale_id}`} onClick={() => setDetailSale(sale)}>
                  <CardContent className="p-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono text-sm font-bold">{sale.invoice_number || sale.sale_id.slice(0, 8)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.color}`}>{b.label}</span>
                        </div>
                        <p className="text-xs text-[#64748B]">{sale.items?.length || 0} ítem(s) · {new Date(sale.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-2">
                        <p className="font-bold text-lg">${sale.total.toFixed(2)}</p>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); downloadTicket(sale.sale_id); }} data-testid={`ticket-${sale.sale_id}`}>
                          <FileText size={14} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Modal detalle */}
      {detailSale && (
        <Dialog open={!!detailSale} onOpenChange={() => setDetailSale(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{detailSale.invoice_number || detailSale.sale_id.slice(0, 8)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <p><b>Estado:</b> <span className={`px-2 py-0.5 rounded text-xs ${statusBadge(detailSale.status).color}`}>{statusBadge(detailSale.status).label}</span></p>
              <p><b>Pago:</b> {detailSale.payment_status} · <b>Pagado:</b> ${(detailSale.amount_paid || 0).toFixed(2)} / ${detailSale.total.toFixed(2)}</p>
              <div className="border rounded p-2 max-h-60 overflow-y-auto">
                {detailSale.items?.map((it, i) => (
                  <div key={i} className="flex justify-between text-xs border-b py-1">
                    <span>{it.name} × {it.quantity}</span>
                    <span className="font-mono">${((it.price || 0) * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <Button onClick={() => downloadTicket(detailSale.sale_id)} className="w-full">
                <FileText size={14} className="mr-2" /> Descargar ticket PDF
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
